/**
 * React component that is responsible for rendering a footer.
 *
 * Copyright (c) 2020 Coursera
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types'
import cx from 'classnames'
import Grid from '@material-ui/core/Grid'
import Container from '@material-ui/core/Container'
import Typography from 'components/theme/Typography'
import FooterLogo from 'static/images/logos/coursera-wordmark-black.svg'
import AppStoreLogo from 'static/images/logos/app_store.svg'
import GooglePlayLogo from 'static/images/logos/google_play.svg'
import BCorp from 'static/images/logos/b-corp.png'
import styles from './Footer.module.scss'

const Footer = ({
  statement,
  linkSections,
}) => {
  const CourseraDetailsSection = useMemo(() => (
    <div>
      <img className={cx(styles.courseraImage)} src={FooterLogo} alt="coursera" />
      <Typography variant="body1" className={cx('mt-md mb-lg')}>
        {statement}
      </Typography>
      <Typography variant="body1">
        &copy;
        {` ${new Date().getFullYear()} `}
        Coursera Inc. All rights reserved.
      </Typography>
      <div className={cx('my-xl')}>
        <a
          className={cx(styles.linkText)}
          href="https://itunes.apple.com/app/apple-store/id736535961?mt=8"
          rel="noopener noreferrer"
          target="_blank">
          <img
            className={cx(styles.externalImage, styles.appStoreImage)}
            src={AppStoreLogo}
            alt="Download on the App Store" />
        </a>
        <a
          className={cx(styles.linkText)}
          href="http://play.google.com/store/apps/details?id=org.coursera.android"
          rel="noopener noreferrer"
          target="_blank">
          <img
            className={cx(styles.externalImage)}
            src={GooglePlayLogo}
            alt="Get it on Google Play" />
        </a>
        <div>
          <img className={cx(styles.bCorp)} src={BCorp} alt="Certified B Corporation" />
        </div>
      </div>
    </div>
  ), [statement])

  const Links = linkSections.map((linkSection, index) => (
    <Grid key={`footerLinkSection-${index}`} item xs={6} sm={3}>
      {linkSection}
    </Grid>
  ))

  return (
    <footer className={cx(styles.footerContainer)}>
      <div className={cx(styles.gridContainer)}>
        <Container>
          <Grid container direction="row" justify="space-between">
            <Grid item xs={12} md={4}>
              {CourseraDetailsSection}
            </Grid>
            {Links.length > 0 ? (
              <Grid container item direction="row" spacing={2} md={7}>
                {Links}
              </Grid>
            ) : null}
          </Grid>
        </Container>
      </div>
    </footer>
  )
}

Footer.propTypes = {
  statement: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  linkSections: PropTypes.array,
}

Footer.defaultProps = {
  linkSections: [],
}

export default Footer
